import {axisBottom, Axis, axisLeft} from 'd3-axis';
import {ScaleLinear, scaleLinear} from 'd3-scale';
import {Line, line, curveCardinal} from 'd3-shape';
import {
    forceSimulation,
    Simulation,
    SimulationLinkDatum,
    forceLink, ForceLink, forceCollide, forceCenter, ForceCollide
} from 'd3-force';
import {select} from 'd3-selection';
import {List} from 'immutable';
import {ICurve, IDischargeChartPoint, ILabeledNode} from './discharge.types';
import {OnChanges, IChangeIndication} from '../common/common.types';
import {getDischargePoints, getDischargeValue} from './utils';
import {DischargeCurveSettingsDialog} from './curve-settings-dialog.controller';
import {IDischargeCurveSettings} from './curve.controller';
import {IDischargeModelItem} from './model-list/model-list.controller';
import {IDischargeDataListCurveController} from './discharge-data-list-curve.directive';
import {IDischargeModelListCurveController} from './model-list-curve.directive';

const curveSettingsDialogTemplate = require<string>(
    './curve-settings-dialog.html');

export interface IDischargeCurveController {
  onDischargeChartPointsChange(dischargeChartPoints: List<IDischargeChartPoint>): void;
  onDischargeModelItemsChange(dischargeModelItems: List<IDischargeModelItem>): void;
}

export interface IDischargeCurveSettings {
    minWaterLevel: number;
    maxWaterLevel: number;
    minDischarge: number;
    maxDischarge: number;
    fitViewportToData: boolean;
}

export default class DischargeCurveController implements OnChanges, IDischargeCurveController {
  static $inject = ['$element', '$scope', '$mdDialog'];

  static HEIGHT = 450;
  static WIDTH = 800;
  static MINOR_MARGIN = 20;
  static MAJOR_MARGIN = 60;
  static DISCHARGE_CURVE_RESOLUTION = 100;

  private postLinkDone: boolean;
  private xScale: ScaleLinear<number, number>;
  private yScale: ScaleLinear<number, number>;
  private xAxis: Axis<number>;
  private yAxis: Axis<number>;
  private lineGenerator: Line<IDischargeChartPoint>;
  private forceSimulator: Simulation<
      ILabeledNode, SimulationLinkDatum<ILabeledNode>>;
  private referencePoint: SVGPoint;
  private curveSettings: IDischargeCurveSettings;
  private dischargeDataListCurveController: IDischargeDataListCurveController;
  private dischargeModelListCurveController: IDischargeModelListCurveController;

  dischargeModels: List<IDischargeModelItem>;
  dischargeData: List<IDischargeChartPoint>;
  dischargeCurves: ICurve[];
  dischargePoints: ILabeledNode[];
  dischargeLabels: ILabeledNode[];
  cursor: IDischargeChartPoint;
  onBoundsChange: (callbackArgs: {
    curveSettings: IDischargeCurveSettings, userChange: boolean}) => void;

  constructor(private $element: ng.IAugmentedJQuery,
              private $scope: ng.IScope,
              private $mdDialog: ng.material.IDialogService) {
    this.xScale = scaleLinear().range([0, this.innerWidth]);
    this.yScale = scaleLinear().range([this.innerHeight, 0]);
    this.xAxis = axisBottom<number>(this.xScale).ticks(5)
        .tickSizeInner(this.innerHeight).tickSizeOuter(0);
    this.yAxis = axisLeft<number>(this.yScale).ticks(5)
        .tickSizeInner(this.innerWidth).tickSizeOuter(0);
    this.lineGenerator = line<IDischargeChartPoint>()
        .x(p => this.xScale(p.discharge))
        .y(p => this.yScale(p.waterLevel)).curve(curveCardinal);
    this.cursor = {
      discharge: 0,
      waterLevel: 0
    };

    if(!this.curveSettings) {
      this.curveSettings = {
        fitViewportToData: true,
        minWaterLevel: 0,
        maxWaterLevel: 100,
        minDischarge: 0,
        maxDischarge: 100
      };
    }

  }

  get height(): number {
    return DischargeCurveController.HEIGHT;
  }

  get width(): number {
    return DischargeCurveController.WIDTH;
  }

  get minorMargin(): number {
    return DischargeCurveController.MINOR_MARGIN;
  }

  get majorMargin(): number {
    return DischargeCurveController.MAJOR_MARGIN;
  }

  get innerHeight(): number {
    return this.height - this.minorMargin - this.majorMargin;
  }

  get innerWidth(): number {
    return this.width - this.minorMargin - this.majorMargin;
  }

  $onInit() {
    if (this.dischargeDataListCurveController) {
      this.dischargeDataListCurveController.addDischargeCurveController(this);
    }
    if (this.dischargeModelListCurveController) {
      this.dischargeModelListCurveController.addDischargeCurveController(this);
    }
  }

  $onChanges(changes: IChangeIndication) {

    if(changes.hasOwnProperty('curveSettings') && changes['curveSettings'].currentValue) {
      this.curveSettings = changes['curveSettings'].currentValue;
    }

    this.dischargeData = this.dischargeData || List<IDischargeChartPoint>();
    this.dischargeModels = this.dischargeModels || List<IDischargeModelItem>();
    this.updateViewportAndElements();
  }

  onDischargeChartPointsChange(dischargeChartPoints: List<IDischargeChartPoint>) {
    this.dischargeData = dischargeChartPoints || List<IDischargeChartPoint>();
    this.updateViewportAndElements();
  }

  onDischargeModelItemsChange(dischargeModelItems: List<IDischargeModelItem>) {
    this.dischargeModels = dischargeModelItems || List<IDischargeModelItem>();
    this.updateViewportAndElements();
  }

  $postLink() {
    this.postLinkDone = true;
    this.drawAxis('x');
    this.drawAxis('y');
    const svg = <SVGSVGElement>this.$element[0].querySelector('svg');
    this.referencePoint = svg.createSVGPoint();
  }

  onMousemove = ($event: MouseEvent) => {
    const rect = <SVGRectElement>$event.target;
    this.referencePoint.x = $event.clientX;
    this.referencePoint.y = $event.clientY;
    this.referencePoint = this.referencePoint.matrixTransform(
        rect.getScreenCTM().inverse());
    this.cursor.discharge = this.xScale.invert(this.referencePoint.x);
    this.cursor.waterLevel = this.yScale.invert(this.referencePoint.y);
  };

  onMouseout = () => {
    this.cursor.discharge = 0;
    this.cursor.waterLevel = 0;
  };

  showChartSettings = ($event: MouseEvent) => {
    this.$mdDialog.show({
      targetEvent: $event,
      template: curveSettingsDialogTemplate,
      controller: DischargeCurveSettingsDialog,
      bindToController: true,
      controllerAs: '$ctrl',
      locals: {
        curveSettings: angular.copy(this.curveSettings)
      }
    }).then((settings: IDischargeCurveSettings) => {

      if (settings.fitViewportToData) {
        this.curveSettings.fitViewportToData = settings.fitViewportToData;
        this.computeBounds();
        this.updateViewElements();
      } else {
        this.curveSettings = settings;
        this.updateViewElements();
      }

      this.onBoundsChange({
        curveSettings: this.curveSettings,
        userChange: true
      });
    });
  };

  private updateViewportAndElements() {
    if (this.curveSettings.fitViewportToData) {
      this.computeBounds();
      this.onBoundsChange({
        curveSettings: this.curveSettings,
        userChange: false
      });
    }
    this.updateViewElements();
  }

  private updateViewElements() {
    this.updateAxes();
    this.updateCurves();
    this.updatePoints();
  }

  private updateAxes() {
    this.yScale.domain([this.curveSettings.minWaterLevel,
      this.curveSettings.maxWaterLevel]);
    this.yAxis.scale(this.yScale);
    this.drawAxis('y');
    this.xScale.domain([this.curveSettings.minDischarge,
      this.curveSettings.maxDischarge]);
    this.xAxis.scale(this.xScale);
    this.drawAxis('x');
  }

  private drawAxis(axis: 'x'|'y') {
    if (this.postLinkDone) {
      const element = select(this.$element[0]);
      element.select(`.${axis}.axis`)
          .call(axis == 'x' ? this.xAxis : this.yAxis);
    }
  }

  private updateCurves() {
    this.dischargeCurves = this.dischargeModels.map(
        (dischargeModelItem, index) => {
          return {
            path: this.lineGenerator(
                getDischargePoints(this.curveSettings.minWaterLevel,
                    this.curveSettings.maxWaterLevel,
                    dischargeModelItem.dischargeModel,
                    DischargeCurveController.DISCHARGE_CURVE_RESOLUTION)),
            strokeColor: dischargeModelItem.colors.backgroundColor
          };
        }).toArray();
  }

  private updatePoints() {
    this.dischargePoints = this.dischargeData.map(datum => {
      return {
        x: this.xScale(datum.discharge),
        y: this.yScale(datum.waterLevel),
        fx: this.xScale(datum.discharge),
        fy: this.yScale(datum.waterLevel),
        selected: datum.selected
      }
    }).toArray();
    this.dischargeLabels = this.dischargeData.map(datum => {
      return {
        x: this.xScale(datum.discharge),
        y: this.yScale(datum.waterLevel),
        label: datum.label
      }
    }).toArray();
    const forceNodes = [].concat(this.dischargePoints, this.dischargeLabels);
    const links: SimulationLinkDatum<ILabeledNode>[] = _.zip(
        this.dischargePoints, this.dischargeLabels)
        .map(([point, label]: [ILabeledNode, ILabeledNode]) => {
          return {
            source: point,
            target: label
          };
        });
    if (this.forceSimulator) {
      this.forceSimulator.stop();
    }
    const link: ForceLink<
        ILabeledNode, SimulationLinkDatum<ILabeledNode>> =
        forceLink(links).distance(30).iterations(10);
    const collide: ForceCollide<ILabeledNode> =
        forceCollide().radius(20).iterations(5);
    this.forceSimulator = forceSimulation(forceNodes)
        .force('link', link)
        .force('collide', collide)
        .on('tick', () => {
          this.$scope.$applyAsync();
        })
        .on('end', () => {
        });
  }

  private computeBounds() {
    let bounds = {
      minWaterLevel: 0,
      maxWaterLevel: 100,
      minDischarge: 0,
      maxDischarge: 0
    };
    if (this.dischargeData.size == 0 && this.dischargeModels.size == 0) {
      bounds.maxDischarge = 100;
    }
    if (this.dischargeData.size == 1) {
      bounds.minWaterLevel = 0;
      bounds.maxWaterLevel = 2 * this.dischargeData.get(0).waterLevel;
      bounds.minDischarge = 0;
      bounds.maxDischarge = 2 * this.dischargeData.get(0).discharge;
    } else if (this.dischargeData.size > 1) {
      bounds = {
        minWaterLevel: Number.MAX_VALUE,
        maxWaterLevel: Number.MIN_VALUE,
        minDischarge: Number.MAX_VALUE,
        maxDischarge: Number.MIN_VALUE
      };
      bounds = this.dischargeData.reduce(
          (prevBounds, datum) => {
            return {
              minWaterLevel: Math
                  .min(prevBounds.minWaterLevel, datum.waterLevel),
              maxWaterLevel: Math
                  .max(prevBounds.maxWaterLevel, datum.waterLevel),
              minDischarge: Math.min(prevBounds.minDischarge, datum.discharge),
              maxDischarge: Math.max(prevBounds.maxDischarge, datum.discharge)
            };
          }, bounds);
      bounds.minWaterLevel = Math.max(0, bounds.minWaterLevel * 0.98);
      bounds.maxWaterLevel *= 1.02;
      bounds.minDischarge = Math.max(0, bounds.minDischarge * 0.98);
      bounds.maxDischarge *= 1.02;
    }
    bounds = this.dischargeModels.reduce(
        (prevBounds, dischargeModelItem) => {
          let minModelDischarge = getDischargeValue(
              prevBounds.minWaterLevel, dischargeModelItem.dischargeModel);
          let maxModelDischarge = getDischargeValue(
              prevBounds.maxWaterLevel, dischargeModelItem.dischargeModel);
          if (minModelDischarge > maxModelDischarge) {
            const tmp = minModelDischarge;
            minModelDischarge = maxModelDischarge;
            maxModelDischarge = tmp;
          }
          return {
            minWaterLevel: prevBounds.minWaterLevel,
            maxWaterLevel: prevBounds.maxWaterLevel,
            minDischarge: Math.min(prevBounds.minDischarge, minModelDischarge),
            maxDischarge: Math.max(prevBounds.maxDischarge, maxModelDischarge)
          };
        }, bounds);
    this.curveSettings.minWaterLevel = bounds.minWaterLevel;
    this.curveSettings.maxWaterLevel = bounds.maxWaterLevel;
    this.curveSettings.minDischarge = bounds.minDischarge;
    this.curveSettings.maxDischarge = bounds.maxDischarge;
  }

}



/** WEBPACK FOOTER **
 ** ./app/discharge/curve.controller.ts
 **/